import { ComponentProps, FunctionComponent, useEffect } from 'react';
import { PropertyReportToggleCta } from '../../../components/PropertyReportToggleCta/PropertyReportToggleCta';
import { Button, mediaHelper } from '@property.com.au/ui-kit';
import { BeforeYouBuyCta } from './subcomponents/BeforeYouBuyCta/BeforeYouBuyCta';
import {
  openSetRelationshipModalWithMyHomePreselectedUrlHash,
  ThisIsMyHomeCta,
} from './subcomponents/ThisIsMyHomeCta/ThisIsMyHomeCta';
import styled from 'styled-components';
import { WebLink } from '../../../app-kit/components/WebLink/WebLink';
import { getFeatureButtonStyle } from '../../../app-kit/helpers/featureButtonStyle';
import { useFollowedProperties } from '../../hooks/useFollowedProperties';
import { useAccountQuery } from '../../../hooks/useAccountQuery';
import { isNotNullOrUndefinedValue } from '../../../helpers/isNotNullOrUndefinedValue';
import { useUrlContext } from '../../../contexts/UrlContext/UrlContext';
import { usePropertyId } from '../../../contexts/PropertyIdContext/PropertyIdContext';
import { usePropertyPageModalContext } from '../../contexts/PropertyPageModalContext/PropertyPageModalContext';
import { useWaitForHydrationWorkaround } from '../PropertyRelationship/useWaitForHydrationWorkaround';
import { useTrackPropertyRelationshipModal } from '../PropertyRelationship/useTrackPropertyRelationshipModal';
import { updatePropertyRelationshipModalPageSource } from '../../../components/PropertyRelationship/UpdatePropertyRelationshipModal/UpdatePropertyRelationshipModalPageSource';
import { followPropertyModalPageSource } from '../../../components/PropertyRelationship/FollowPropertyModal/FollowPropertyModalPageSource';
import { replace } from '@rea-argonaut/link';

export enum CtaType {
  toggle = 'toggle',
  link = 'link',
  button = 'button',
}

export interface CtaToggleViewProps extends ComponentProps<typeof PropertyReportToggleCta> {
  type: CtaType.toggle;
}

export interface CtaLinkViewProps extends ComponentProps<typeof WebLink> {
  type: CtaType.link;
  isFeatured: boolean;
}

export interface CtaButtonViewProps extends ComponentProps<typeof Button> {
  type: CtaType.button;
  ctaVariant: 'BeforeYouBuy' | 'BuildingAndPest' | 'ThisIsMyHome';
  text: string;
}

export type CtaList = (CtaToggleViewProps | CtaLinkViewProps | CtaButtonViewProps)[];

type HeaderCtasV2Props = {
  ctas: CtaList;
};

const StyledWebLink = styled(WebLink)<{ $isFeatured: boolean }>`
  width: 100%;
  ${mediaHelper('tabletMin')} {
    width: auto;
  }
  ${({ $isFeatured }) => $isFeatured && getFeatureButtonStyle()}
`;

export const HeaderCtasV2: FunctionComponent<HeaderCtasV2Props> = ({ ctas }) => {
  const { followedProperties } = useFollowedProperties();
  const accountQueryResult = useAccountQuery();
  const signedIn = isNotNullOrUndefinedValue(accountQueryResult.data?.account);
  const url = useUrlContext();
  const propertyId = usePropertyId();
  const { displayModal } = usePropertyPageModalContext();
  const isHydrated = useWaitForHydrationWorkaround();
  const trackPropertyRelationshipModal = useTrackPropertyRelationshipModal();

  const propertyRelationship = followedProperties?.find(
    ({ id }) => id === propertyId,
  )?.relationship;
  const isFollowed = propertyRelationship !== undefined;

  useEffect(() => {
    if (
      isHydrated &&
      signedIn &&
      url.hash === openSetRelationshipModalWithMyHomePreselectedUrlHash
    ) {
      if (isFollowed) {
        displayModal({ name: 'updatePropertyRelationshipModalMyHomeAsDefault' });
        trackPropertyRelationshipModal({
          user_action: 'opened',
          modal_name: updatePropertyRelationshipModalPageSource,
          screen_name: 'property_page',
          in_page_source: ['property_page', 'url_hash'].join('__'),
        });
      } else {
        displayModal({ name: 'followPropertyModalWithMyHomeAsDefault' });
        trackPropertyRelationshipModal({
          user_action: 'opened',
          modal_name: followPropertyModalPageSource,
          screen_name: 'property_page',
          in_page_source: ['property_page', 'url_hash'].join('__'),
        });
      }
      replace(location.pathname + location.search);
    }
  }, [signedIn, isHydrated, url.hash, displayModal, trackPropertyRelationshipModal, isFollowed]);

  return ctas.map((cta) => {
    if (cta.type === CtaType.toggle) {
      return <PropertyReportToggleCta {...cta} key={cta.type} />;
    }
    if (cta.type === CtaType.link) {
      return (
        <StyledWebLink
          {...cta}
          $isFeatured={cta.isFeatured}
          clientSideRouting={false}
          key={cta.title}
        />
      );
    }

    if (cta.type === CtaType.button && cta.ctaVariant === 'BeforeYouBuy') {
      return <BeforeYouBuyCta cta={cta} key={cta.text} />;
    }

    if (cta.type === CtaType.button && cta.ctaVariant === 'ThisIsMyHome') {
      return <ThisIsMyHomeCta cta={cta} key={cta.text} />;
    }

    return null;
  });
};
